import React, { useEffect, useState } from 'react';
import {
  Venue,
  VenueSchedule,
  BlockoutRule,
  BlockoutType,
  CapacityRule,
  DaySchedule,
  SpecialOpening
} from '@/types';
import { Plus, Edit2, Trash2, MapPin, Calendar, Clock, X, Loader2, Search } from 'lucide-react';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { normalizeSlotIntervalMinutes } from '@/lib/scheduling';
import { createId } from '@/lib/ids';
import { getGoogleMapsUrl, getVenueMapUrl, hasValidCoordinates } from '@/lib/venueMaps';
import { formatUkDate } from '@/lib/dates';
import { api } from '@/services/api';
import { Pagination } from '@shared/components/common/Pagination';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface VenuesTabProps {
  onAddVenue: (venue: Venue) => void;
  onUpdateVenue: (venue: Venue) => void;
  onDeleteVenue: (id: string) => void;
  canEdit: boolean;
}

const DEFAULT_SCHEDULE: VenueSchedule = {
  weekly: {
    0: { open: false, start: '09:00', end: '17:00', capacity: 1 },
    1: { open: true, start: '09:00', end: '17:00', capacity: 1 },
    2: { open: true, start: '09:00', end: '17:00', capacity: 1 },
    3: { open: true, start: '09:00', end: '17:00', capacity: 1 },
    4: { open: true, start: '09:00', end: '17:00', capacity: 1 },
    5: { open: true, start: '09:00', end: '16:00', capacity: 1 },
    6: { open: false, start: '09:00', end: '17:00', capacity: 1 }
  },
  blockouts: [],
  slotIntervalMinutes: 30,
  capacityRules: [],
  specialOpenings: []
};

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
type VenueRuleFrequency = NonNullable<BlockoutRule['frequency']>;
const isVenueRuleFrequency = (value: string): value is VenueRuleFrequency => {
  return value === 'every' || value === 'biweekly';
};
type DayScheduleField = Extract<keyof DaySchedule, 'open' | 'start' | 'end' | 'capacity'>;
type DayScheduleValue = boolean | string | number;

const normalizeDayCapacity = (value?: number) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(50, Math.max(0, Math.floor(parsed)));
};

const timeToMinutes = (value: string) => {
  const [hours, minutes] = value.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const rangesOverlap = (startA: string, endA: string, startB: string, endB: string) => {
  const startAMin = timeToMinutes(startA);
  const endAMin = timeToMinutes(endA);
  const startBMin = timeToMinutes(startB);
  const endBMin = timeToMinutes(endB);

  if (startAMin == null || endAMin == null || startBMin == null || endBMin == null) {
    return false;
  }

  return startAMin < endBMin && endAMin > startBMin;
};

const parseCoordinatesDraft = (draft: { lat: string; lng: string }) => {
  const latRaw = String(draft.lat || '').trim();
  const lngRaw = String(draft.lng || '').trim();
  const hasValue = Boolean(latRaw || lngRaw);

  if (!hasValue) {
    return { coords: null, hasValue: false, isValid: true };
  }

  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  const withinRange = lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !withinRange) {
    return { coords: null, hasValue: true, isValid: false };
  }

  return { coords: { lat, lng }, hasValue: true, isValid: true };
};

const getScheduleWithDefaults = (schedule?: VenueSchedule): VenueSchedule => {
  const weekly = {
    ...DEFAULT_SCHEDULE.weekly,
    ...(schedule?.weekly || {})
  };

  const normalizedWeekly = Object.keys(DEFAULT_SCHEDULE.weekly).reduce(
    (acc, key) => {
      const dayKey = Number(key);
      const day = weekly[dayKey] || DEFAULT_SCHEDULE.weekly[dayKey];
      acc[dayKey] = {
        ...day,
        capacity: normalizeDayCapacity(
          day.capacity ?? DEFAULT_SCHEDULE.weekly[dayKey].capacity ?? 1
        )
      };
      return acc;
    },
    {} as Record<number, DaySchedule>
  );

  return {
    ...DEFAULT_SCHEDULE,
    ...schedule,
    weekly: normalizedWeekly,
    blockouts: [...(schedule?.blockouts || [])],
    capacityRules: [...(schedule?.capacityRules || [])],
    specialOpenings: [...(schedule?.specialOpenings || [])],
    slotIntervalMinutes: normalizeSlotIntervalMinutes(
      schedule?.slotIntervalMinutes ?? DEFAULT_SCHEDULE.slotIntervalMinutes
    )
  };
};

const VenueMapPreview = ({ venue }: { venue: Venue }) => {
  const mapUrl = getVenueMapUrl(venue, {
    width: 360,
    height: 220,
    zoom: 15
  });
  const mapsLink = getGoogleMapsUrl(venue);
  const hasAddress = Boolean(venue.address || venue.city);
  const hasCoordinates = hasValidCoordinates(venue.coordinates);
  const statusMessage = hasCoordinates
    ? 'Preview uses current coordinates. Save changes to persist.'
    : hasAddress
      ? 'Save venue to generate a map preview.'
      : 'Enter address and city to generate a preview.';

  return (
    <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
      <div className="w-28 h-20 border rounded-lg bg-white flex items-center justify-center overflow-hidden shadow-inner shrink-0">
        {mapUrl ? (
          mapsLink ? (
            <a
              href={mapsLink}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${venue.name} in Google Maps`}
              className="block w-full h-full"
            >
              <img src={mapUrl} alt="Map preview" className="w-full h-full object-cover" />
            </a>
          ) : (
            <img src={mapUrl} alt="Map preview" className="w-full h-full object-cover" />
          )
        ) : (
          <div className="bg-slate-100 w-full h-full flex items-center justify-center">
            <MapPin className="w-6 h-6 text-slate-300" />
          </div>
        )}
      </div>
      <div className="flex-1">
        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">
          OpenStreetMap Preview
        </p>
        <p className="text-[9px] text-slate-400 leading-tight mt-1">{statusMessage}</p>
      </div>
    </div>
  );
};

export const VenuesTab: React.FC<VenuesTabProps> = ({
  onAddVenue,
  onUpdateVenue,
  onDeleteVenue,
  canEdit
}) => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoadingVenues, setIsLoadingVenues] = useState(false);
  const [venuesError, setVenuesError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'schedule'>('details');
  const [isSaving, setIsSaving] = useState(false);
  const [coordinatesDraft, setCoordinatesDraft] = useState({ lat: '', lng: '' });

  // Blockout Rule State
  const [newRuleType, setNewRuleType] = useState<BlockoutType>('single');
  const [newRuleData, setNewRuleData] = useState<Partial<BlockoutRule>>({});
  const [newCapacityRule, setNewCapacityRule] = useState<Partial<CapacityRule>>({
    dayOfWeek: 1,
    start: '09:00',
    end: '12:00',
    capacity: 1,
    slotIntervalMinutes: undefined
  });
  const [newSpecialOpening, setNewSpecialOpening] = useState<Partial<SpecialOpening>>({
    date: '',
    start: '09:00',
    end: '17:00',
    capacity: undefined,
    label: ''
  });

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingVenues(true);
    setVenuesError('');
    api
      .listStaffVenues({ page, pageSize, q: search || undefined })
      .then((data) => {
        if (!isActive) return;
        setVenues(data.items || []);
        setTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setVenues([]);
        setTotalCount(0);
        setVenuesError(normalizeErrorMessage(err, 'Failed to load venues.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingVenues(false);
      });

    return () => {
      isActive = false;
    };
  }, [page, pageSize, search]);

  const openVenueEditor = (venue: Venue, tab: 'details' | 'schedule' = 'details') => {
    setEditingVenue(venue);
    setActiveTab(tab);
    setCoordinatesDraft({
      lat: venue.coordinates?.lat != null ? String(venue.coordinates.lat) : '',
      lng: venue.coordinates?.lng != null ? String(venue.coordinates.lng) : ''
    });
  };

  const refreshVenues = async () => {
    setIsLoadingVenues(true);
    setVenuesError('');
    try {
      const data = await api.listStaffVenues({ page, pageSize, q: search || undefined });
      setVenues(data.items || []);
      setTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setVenues([]);
      setTotalCount(0);
      setVenuesError(normalizeErrorMessage(err, 'Failed to load venues.'));
    } finally {
      setIsLoadingVenues(false);
    }
  };

  const handleAddRule = () => {
    if (!editingVenue) return;

    // Validation
    if (newRuleType === 'single' && !newRuleData.date) return showAlertModal('Select a date');
    if (newRuleType === 'range' && (!newRuleData.startDate || !newRuleData.endDate))
      return showAlertModal('Select start and end dates');
    if (
      newRuleType === 'pattern' &&
      (newRuleData.dayOfWeek === undefined ||
        !newRuleData.frequency ||
        (newRuleData.frequency === 'biweekly' && !newRuleData.startFrom))
    )
      return showAlertModal('Complete pattern details');

    const newRule: BlockoutRule = {
      id: createId(),
      type: newRuleType,
      reason: newRuleData.reason || 'Closed',
      ...newRuleData
    };

    const currentSchedule = getScheduleWithDefaults(editingVenue.schedule);
    const updatedSchedule = {
      ...currentSchedule,
      blockouts: [...currentSchedule.blockouts, newRule]
    };

    setEditingVenue({ ...editingVenue, schedule: updatedSchedule });
    setNewRuleData({});
  };

  const removeRule = (ruleId: string) => {
    if (!editingVenue) return;
    const currentSchedule = getScheduleWithDefaults(editingVenue.schedule);
    const updatedSchedule = {
      ...currentSchedule,
      blockouts: currentSchedule.blockouts.filter((r) => r.id !== ruleId)
    };
    setEditingVenue({ ...editingVenue, schedule: updatedSchedule });
  };

  const updateDaySchedule = (
    dayIndex: number,
    field: DayScheduleField,
    value: DayScheduleValue
  ) => {
    if (!editingVenue) return;
    const currentSchedule = getScheduleWithDefaults(editingVenue.schedule);
    const nextValue =
      field === 'capacity'
        ? normalizeDayCapacity(typeof value === 'number' ? value : Number(value))
        : value;
    const updatedWeekly = {
      ...currentSchedule.weekly,
      [dayIndex]: { ...currentSchedule.weekly[dayIndex], [field]: nextValue }
    };
    setEditingVenue({ ...editingVenue, schedule: { ...currentSchedule, weekly: updatedWeekly } });
  };

  const updateSlotIntervalMinutes = (value: number) => {
    if (!editingVenue) return;
    const currentSchedule = getScheduleWithDefaults(editingVenue.schedule);
    setEditingVenue({
      ...editingVenue,
      schedule: {
        ...currentSchedule,
        slotIntervalMinutes: normalizeSlotIntervalMinutes(value)
      }
    });
  };

  const addCapacityRule = () => {
    if (!editingVenue) return;

    const dayOfWeek = Number(newCapacityRule.dayOfWeek);
    const start = String(newCapacityRule.start || '');
    const end = String(newCapacityRule.end || '');
    const capacity = Number(newCapacityRule.capacity);
    const rawSlotInterval = newCapacityRule.slotIntervalMinutes;
    const parsedSlotInterval = rawSlotInterval == null ? null : Number(rawSlotInterval);

    if (!Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) {
      return showAlertModal('Select a valid day for the capacity block.');
    }

    if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) {
      return showAlertModal('Select valid start and end times for the capacity block.');
    }

    if (end <= start) {
      return showAlertModal('Capacity block end time must be later than start time.');
    }

    if (!Number.isFinite(capacity) || capacity < 0 || capacity > 50) {
      return showAlertModal('Capacity must be between 0 and 50.');
    }

    if (
      parsedSlotInterval != null &&
      (!Number.isFinite(parsedSlotInterval) || parsedSlotInterval < 5 || parsedSlotInterval > 180)
    ) {
      return showAlertModal('Slot length must be between 5 and 180 minutes.');
    }

    const currentSchedule = getScheduleWithDefaults(editingVenue.schedule);
    if (parsedSlotInterval != null) {
      const overlappingRule = (currentSchedule.capacityRules || []).find((rule) => {
        if (rule.dayOfWeek !== dayOfWeek) return false;
        if (rule.slotIntervalMinutes == null) return false;
        return rangesOverlap(start, end, rule.start, rule.end);
      });

      if (overlappingRule) {
        return showAlertModal(
          'Slot length overrides cannot overlap. Adjust the time range or remove the other slot-length rule.'
        );
      }
    }

    const newRule: CapacityRule = {
      id: createId(),
      dayOfWeek,
      start,
      end,
      capacity: Math.floor(capacity),
      label: String(newCapacityRule.label || '').trim() || undefined,
      slotIntervalMinutes:
        parsedSlotInterval != null ? normalizeSlotIntervalMinutes(parsedSlotInterval) : undefined
    };

    setEditingVenue({
      ...editingVenue,
      schedule: {
        ...currentSchedule,
        capacityRules: [...(currentSchedule.capacityRules || []), newRule]
      }
    });

    setNewCapacityRule({
      dayOfWeek,
      start: '09:00',
      end: '12:00',
      capacity: 1,
      label: '',
      slotIntervalMinutes: undefined
    });
  };

  const removeCapacityRule = (ruleId: string) => {
    if (!editingVenue) return;

    const currentSchedule = getScheduleWithDefaults(editingVenue.schedule);
    setEditingVenue({
      ...editingVenue,
      schedule: {
        ...currentSchedule,
        capacityRules: (currentSchedule.capacityRules || []).filter((rule) => rule.id !== ruleId)
      }
    });
  };

  const addSpecialOpening = () => {
    if (!editingVenue) return;

    const date = String(newSpecialOpening.date || '');
    const start = String(newSpecialOpening.start || '');
    const end = String(newSpecialOpening.end || '');
    const capacity = newSpecialOpening.capacity == null ? null : Number(newSpecialOpening.capacity);

    if (!date) {
      return showAlertModal('Select a date for the special opening.');
    }

    if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) {
      return showAlertModal('Select valid start and end times.');
    }

    const startMin = timeToMinutes(start);
    const endMin = timeToMinutes(end);
    if (startMin == null || endMin == null || endMin <= startMin) {
      return showAlertModal('Special opening end time must be later than start time.');
    }

    if (capacity != null && (!Number.isFinite(capacity) || capacity < 0 || capacity > 50)) {
      return showAlertModal('Capacity must be between 0 and 50.');
    }

    const currentSchedule = getScheduleWithDefaults(editingVenue.schedule);
    const existingOpening = (currentSchedule.specialOpenings || []).find(
      (opening) => opening.date === date
    );
    if (existingOpening) {
      return showAlertModal('A special opening already exists for this date.');
    }

    const newOpening: SpecialOpening = {
      id: createId(),
      date,
      start,
      end,
      capacity: capacity == null ? undefined : Math.floor(capacity),
      label: String(newSpecialOpening.label || '').trim() || undefined
    };

    setEditingVenue({
      ...editingVenue,
      schedule: {
        ...currentSchedule,
        specialOpenings: [...(currentSchedule.specialOpenings || []), newOpening]
      }
    });

    setNewSpecialOpening({
      date: '',
      start: '09:00',
      end: '17:00',
      capacity: undefined,
      label: ''
    });
  };

  const removeSpecialOpening = (openingId: string) => {
    if (!editingVenue) return;

    const currentSchedule = getScheduleWithDefaults(editingVenue.schedule);
    setEditingVenue({
      ...editingVenue,
      schedule: {
        ...currentSchedule,
        specialOpenings: (currentSchedule.specialOpenings || []).filter(
          (opening) => opening.id !== openingId
        )
      }
    });
  };

  return (
    <div className="animate-fade-in">
      {editingVenue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-fade-in">
            <div className="p-4 border-b bg-slate-50 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-800">
                {editingVenue.id ? 'Edit Venue' : 'Add New Venue'}
              </h3>
              <button onClick={() => setEditingVenue(null)}>
                <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>

            <div className="flex border-b bg-white">
              <button
                onClick={() => setActiveTab('details')}
                className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-all ${activeTab === 'details' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
              >
                Venue Details
              </button>
              <button
                onClick={() => setActiveTab('schedule')}
                className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-all ${activeTab === 'schedule' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
              >
                Opening Hours & Blockouts
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {activeTab === 'details' ? (
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                      Venue Name
                    </label>
                    <input
                      className="w-full border p-2.5 rounded-lg bg-slate-50 focus:ring-2 focus:ring-teal-500 outline-none"
                      placeholder="Name"
                      value={editingVenue.name}
                      onChange={(e) => setEditingVenue({ ...editingVenue, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                      Address
                    </label>
                    <input
                      className="w-full border p-2.5 rounded-lg bg-slate-50 focus:ring-2 focus:ring-teal-500 outline-none"
                      placeholder="Address"
                      value={editingVenue.address}
                      onChange={(e) =>
                        setEditingVenue({ ...editingVenue, address: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                      City
                    </label>
                    <input
                      className="w-full border p-2.5 rounded-lg bg-slate-50 focus:ring-2 focus:ring-teal-500 outline-none"
                      placeholder="City"
                      value={editingVenue.city}
                      onChange={(e) => setEditingVenue({ ...editingVenue, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                      Coordinates (Optional)
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="number"
                        step="0.000001"
                        inputMode="decimal"
                        className="w-full border p-2.5 rounded-lg bg-slate-50 focus:ring-2 focus:ring-teal-500 outline-none"
                        placeholder="Latitude"
                        value={coordinatesDraft.lat}
                        onChange={(e) =>
                          setCoordinatesDraft((prev) => ({ ...prev, lat: e.target.value }))
                        }
                      />
                      <input
                        type="number"
                        step="0.000001"
                        inputMode="decimal"
                        className="w-full border p-2.5 rounded-lg bg-slate-50 focus:ring-2 focus:ring-teal-500 outline-none"
                        placeholder="Longitude"
                        value={coordinatesDraft.lng}
                        onChange={(e) =>
                          setCoordinatesDraft((prev) => ({ ...prev, lng: e.target.value }))
                        }
                      />
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Leave blank to auto-detect from address. Enter both values to override.
                    </p>
                    {(() => {
                      const parsed = parseCoordinatesDraft(coordinatesDraft);
                      if (!parsed.hasValue || parsed.isValid) return null;
                      return (
                        <p className="text-[11px] text-red-500 mt-1">
                          Enter a valid latitude (-90 to 90) and longitude (-180 to 180).
                        </p>
                      );
                    })()}
                  </div>
                  {(() => {
                    const parsed = parseCoordinatesDraft(coordinatesDraft);
                    const previewVenue =
                      parsed.isValid && parsed.coords
                        ? { ...editingVenue, coordinates: parsed.coords }
                        : editingVenue;
                    return <VenueMapPreview venue={previewVenue} />;
                  })()}
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-indigo-500" /> Appointment Slot Interval
                    </h4>
                    <div className="bg-slate-50 border rounded-xl p-4 shadow-inner">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                            Slot Length
                          </p>
                          <p className="text-[11px] text-slate-500 mt-1">
                            Controls booking start times for this location.
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={5}
                            max={180}
                            step={5}
                            className="w-24 border rounded-lg px-2 py-1.5 text-sm font-bold bg-white outline-none focus:ring-2 focus:ring-teal-500"
                            value={
                              getScheduleWithDefaults(editingVenue.schedule).slotIntervalMinutes
                            }
                            onChange={(e) =>
                              updateSlotIntervalMinutes(Number(e.target.value || 30))
                            }
                          />
                          <span className="text-xs font-bold text-slate-500 uppercase">mins</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-teal-600" /> Standard Weekly Hours
                    </h4>
                    <div className="bg-slate-50 border rounded-xl overflow-hidden shadow-inner">
                      {DAYS.map((day, idx) => {
                        const schedule = getScheduleWithDefaults(editingVenue.schedule).weekly[
                          idx
                        ] || { open: false, start: '09:00', end: '17:00' };
                        return (
                          <div
                            key={day}
                            className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-white transition-colors"
                          >
                            <div className="w-24 text-sm font-bold text-slate-700">{day}</div>
                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-teal-600 rounded"
                                  checked={schedule.open}
                                  onChange={(e) => updateDaySchedule(idx, 'open', e.target.checked)}
                                />
                                <span
                                  className={`text-[10px] font-bold tracking-widest ${schedule.open ? 'text-green-600' : 'text-slate-400'}`}
                                >
                                  {schedule.open ? 'OPEN' : 'CLOSED'}
                                </span>
                              </label>
                              {schedule.open && (
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-2 text-sm bg-white border rounded-lg px-2 py-1 shadow-sm">
                                    <input
                                      type="time"
                                      className="border-none p-0 text-xs font-medium outline-none"
                                      value={schedule.start}
                                      onChange={(e) =>
                                        updateDaySchedule(idx, 'start', e.target.value)
                                      }
                                    />
                                    <span className="text-slate-300">-</span>
                                    <input
                                      type="time"
                                      className="border-none p-0 text-xs font-medium outline-none"
                                      value={schedule.end}
                                      onChange={(e) =>
                                        updateDaySchedule(idx, 'end', e.target.value)
                                      }
                                    />
                                  </div>
                                  <div className="flex items-center gap-2 bg-white border rounded-lg px-2 py-1 shadow-sm">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                                      Cap
                                    </span>
                                    <input
                                      type="number"
                                      min={0}
                                      max={50}
                                      className="w-14 border-none p-0 text-xs font-medium outline-none"
                                      value={schedule.capacity ?? 1}
                                      onChange={(e) =>
                                        updateDaySchedule(idx, 'capacity', e.target.value)
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" /> Concurrent Capacity Blocks
                    </h4>
                    <div className="bg-slate-50 border rounded-xl p-4 mb-4 space-y-3 shadow-inner">
                      <p className="text-[11px] text-slate-500">
                        Set how many appointments can run at once in a time range. Use{' '}
                        <strong>0</strong> for breaks (for example lunch 12:00-13:00).
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <select
                          className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          value={newCapacityRule.dayOfWeek ?? 1}
                          onChange={(e) =>
                            setNewCapacityRule((prev) => ({
                              ...prev,
                              dayOfWeek: Number(e.target.value)
                            }))
                          }
                        >
                          {DAYS.map((day, idx) => (
                            <option key={day} value={idx}>
                              {day}
                            </option>
                          ))}
                        </select>
                        <input
                          className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="Label (optional)"
                          value={newCapacityRule.label || ''}
                          onChange={(e) =>
                            setNewCapacityRule((prev) => ({ ...prev, label: e.target.value }))
                          }
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                        <input
                          type="time"
                          className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          value={newCapacityRule.start || '09:00'}
                          onChange={(e) =>
                            setNewCapacityRule((prev) => ({ ...prev, start: e.target.value }))
                          }
                        />
                        <input
                          type="time"
                          className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          value={newCapacityRule.end || '12:00'}
                          onChange={(e) =>
                            setNewCapacityRule((prev) => ({ ...prev, end: e.target.value }))
                          }
                        />
                        <input
                          type="number"
                          min={0}
                          max={50}
                          className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          value={newCapacityRule.capacity ?? 1}
                          onChange={(e) =>
                            setNewCapacityRule((prev) => ({
                              ...prev,
                              capacity: Number(e.target.value || 0)
                            }))
                          }
                        />
                        <input
                          type="number"
                          min={5}
                          max={180}
                          step={5}
                          className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="Slot mins"
                          value={newCapacityRule.slotIntervalMinutes ?? ''}
                          onChange={(e) =>
                            setNewCapacityRule((prev) => ({
                              ...prev,
                              slotIntervalMinutes:
                                e.target.value === '' ? undefined : Number(e.target.value)
                            }))
                          }
                        />
                        <button
                          onClick={addCapacityRule}
                          className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-teal-700 shadow-sm transition-all"
                        >
                          Add Block
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                      {(editingVenue.schedule?.capacityRules || []).length === 0 && (
                        <p className="text-xs text-slate-400 italic text-center py-4 bg-white rounded-xl border border-dashed">
                          No capacity blocks configured.
                        </p>
                      )}
                      {(editingVenue.schedule?.capacityRules || [])
                        .slice()
                        .sort((left, right) => {
                          if (left.dayOfWeek !== right.dayOfWeek) {
                            return left.dayOfWeek - right.dayOfWeek;
                          }
                          return left.start.localeCompare(right.start);
                        })
                        .map((rule) => (
                          <div
                            key={rule.id}
                            className="flex justify-between items-center bg-white border border-slate-200 p-3 rounded-xl shadow-sm group hover:border-teal-300 transition-all"
                          >
                            <div>
                              <div className="font-bold text-slate-800 text-sm">
                                {DAYS[rule.dayOfWeek]} {rule.start}-{rule.end}
                              </div>
                              <div className="text-[10px] font-bold text-slate-400 uppercase mt-0.5 tracking-tight">
                                {rule.capacity === 0
                                  ? `Break ${rule.label ? `- ${rule.label}` : ''}`
                                  : `${rule.capacity} concurrent slot${
                                      rule.capacity === 1 ? '' : 's'
                                    }${rule.label ? ` - ${rule.label}` : ''}${
                                      rule.slotIntervalMinutes
                                        ? ` · ${rule.slotIntervalMinutes} min slots`
                                        : ''
                                    }`}
                              </div>
                            </div>
                            <button
                              onClick={() => removeCapacityRule(rule.id)}
                              className="text-slate-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-emerald-600" /> Special Opening Days
                    </h4>
                    <div className="bg-slate-50 border rounded-xl p-4 mb-4 space-y-3 shadow-inner">
                      <p className="text-[11px] text-slate-500">
                        Add one-off open days. These override weekly closed days and blockouts.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                        <input
                          type="date"
                          className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          value={newSpecialOpening.date || ''}
                          onChange={(e) =>
                            setNewSpecialOpening((prev) => ({
                              ...prev,
                              date: e.target.value
                            }))
                          }
                        />
                        <input
                          type="time"
                          className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          value={newSpecialOpening.start || '09:00'}
                          onChange={(e) =>
                            setNewSpecialOpening((prev) => ({
                              ...prev,
                              start: e.target.value
                            }))
                          }
                        />
                        <input
                          type="time"
                          className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          value={newSpecialOpening.end || '17:00'}
                          onChange={(e) =>
                            setNewSpecialOpening((prev) => ({
                              ...prev,
                              end: e.target.value
                            }))
                          }
                        />
                        <input
                          type="number"
                          min={0}
                          max={50}
                          className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="Cap (opt)"
                          value={
                            newSpecialOpening.capacity == null ? '' : newSpecialOpening.capacity
                          }
                          onChange={(e) =>
                            setNewSpecialOpening((prev) => ({
                              ...prev,
                              capacity:
                                e.target.value === '' ? undefined : Number(e.target.value || 0)
                            }))
                          }
                        />
                        <button
                          onClick={addSpecialOpening}
                          className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-teal-700 shadow-sm transition-all"
                        >
                          Add Day
                        </button>
                      </div>
                      <input
                        className="border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="Label (optional)"
                        value={newSpecialOpening.label || ''}
                        onChange={(e) =>
                          setNewSpecialOpening((prev) => ({ ...prev, label: e.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                      {(editingVenue.schedule?.specialOpenings || []).length === 0 && (
                        <p className="text-xs text-slate-400 italic text-center py-4 bg-white rounded-xl border border-dashed">
                          No special openings configured.
                        </p>
                      )}
                      {(editingVenue.schedule?.specialOpenings || [])
                        .slice()
                        .sort((left, right) => left.date.localeCompare(right.date))
                        .map((opening) => (
                          <div
                            key={opening.id}
                            className="flex justify-between items-center bg-white border border-slate-200 p-3 rounded-xl shadow-sm group hover:border-teal-300 transition-all"
                          >
                            <div>
                              <div className="font-bold text-slate-800 text-sm">
                                {formatUkDate(opening.date, '—')}
                              </div>
                              <div className="text-[10px] font-bold text-slate-400 uppercase mt-0.5 tracking-tight">
                                {opening.start}-{opening.end} ·{' '}
                                {opening.capacity == null
                                  ? 'Default capacity'
                                  : `Cap ${opening.capacity}`}
                                {opening.label ? ` · ${opening.label}` : ''}
                              </div>
                            </div>
                            <button
                              onClick={() => removeSpecialOpening(opening.id)}
                              className="text-slate-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-orange-500" /> Blockout Rules & Holidays
                    </h4>
                    <div className="bg-slate-50 border rounded-xl p-4 mb-4 space-y-4 shadow-inner">
                      <div className="flex gap-2 text-[10px] font-bold uppercase tracking-wider">
                        <button
                          onClick={() => setNewRuleType('single')}
                          className={`px-3 py-1.5 rounded-full transition-all ${newRuleType === 'single' ? 'bg-slate-800 text-white shadow-md' : 'bg-white border text-slate-500 hover:bg-white hover:text-slate-800'}`}
                        >
                          Single Date
                        </button>
                        <button
                          onClick={() => setNewRuleType('range')}
                          className={`px-3 py-1.5 rounded-full transition-all ${newRuleType === 'range' ? 'bg-slate-800 text-white shadow-md' : 'bg-white border text-slate-500 hover:bg-white hover:text-slate-800'}`}
                        >
                          Date Range
                        </button>
                        <button
                          onClick={() => setNewRuleType('pattern')}
                          className={`px-3 py-1.5 rounded-full transition-all ${newRuleType === 'pattern' ? 'bg-slate-800 text-white shadow-md' : 'bg-white border text-slate-500 hover:bg-white hover:text-slate-800'}`}
                        >
                          Recurring
                        </button>
                      </div>
                      <div className="grid gap-3">
                        {newRuleType === 'single' && (
                          <input
                            type="date"
                            className="w-full border rounded-lg p-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500"
                            value={newRuleData.date || ''}
                            onChange={(e) =>
                              setNewRuleData({ ...newRuleData, date: e.target.value })
                            }
                          />
                        )}
                        {newRuleType === 'range' && (
                          <div className="flex gap-3">
                            <input
                              type="date"
                              className="border rounded-lg p-2 text-sm flex-1 bg-white outline-none focus:ring-2 focus:ring-teal-500"
                              placeholder="Start"
                              value={newRuleData.startDate || ''}
                              onChange={(e) =>
                                setNewRuleData({ ...newRuleData, startDate: e.target.value })
                              }
                            />
                            <input
                              type="date"
                              className="border rounded-lg p-2 text-sm flex-1 bg-white outline-none focus:ring-2 focus:ring-teal-500"
                              placeholder="End"
                              value={newRuleData.endDate || ''}
                              onChange={(e) =>
                                setNewRuleData({ ...newRuleData, endDate: e.target.value })
                              }
                            />
                          </div>
                        )}
                        {newRuleType === 'pattern' && (
                          <div className="space-y-3">
                            <div className="flex gap-3">
                              <select
                                className="border rounded-lg p-2 text-sm flex-1 bg-white outline-none focus:ring-2 focus:ring-teal-500"
                                value={newRuleData.dayOfWeek}
                                onChange={(e) =>
                                  setNewRuleData({
                                    ...newRuleData,
                                    dayOfWeek: Number(e.target.value)
                                  })
                                }
                              >
                                <option value="" disabled selected>
                                  Select Day
                                </option>
                                {DAYS.map((d, i) => (
                                  <option key={i} value={i}>
                                    {d}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="border rounded-lg p-2 text-sm flex-1 bg-white outline-none focus:ring-2 focus:ring-teal-500"
                                value={newRuleData.frequency || ''}
                                onChange={(e) => {
                                  const nextFrequency = e.target.value;
                                  setNewRuleData({
                                    ...newRuleData,
                                    frequency: isVenueRuleFrequency(nextFrequency)
                                      ? nextFrequency
                                      : undefined
                                  });
                                }}
                              >
                                <option value="" disabled selected>
                                  Frequency
                                </option>
                                <option value="every">Every Week</option>
                                <option value="biweekly">Bi-Weekly</option>
                              </select>
                            </div>
                            {newRuleData.frequency === 'biweekly' && (
                              <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-200">
                                <label className="text-[10px] font-bold text-slate-400 uppercase">
                                  Start From:
                                </label>
                                <input
                                  type="date"
                                  className="border-none p-0 text-xs font-medium outline-none"
                                  value={newRuleData.startFrom || ''}
                                  onChange={(e) =>
                                    setNewRuleData({ ...newRuleData, startFrom: e.target.value })
                                  }
                                />
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            className="border rounded-lg p-2 text-sm flex-1 bg-white outline-none focus:ring-2 focus:ring-teal-500"
                            placeholder="Reason (e.g. Bank Holiday)"
                            value={newRuleData.reason || ''}
                            onChange={(e) =>
                              setNewRuleData({ ...newRuleData, reason: e.target.value })
                            }
                          />
                          <button
                            onClick={handleAddRule}
                            className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-teal-700 shadow-sm transition-all"
                          >
                            Add Rule
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                      {(editingVenue.schedule?.blockouts || []).length === 0 && (
                        <p className="text-xs text-slate-400 italic text-center py-4 bg-white rounded-xl border border-dashed">
                          No blockout rules active.
                        </p>
                      )}
                      {(editingVenue.schedule?.blockouts || []).map((rule) => (
                        <div
                          key={rule.id}
                          className="flex justify-between items-center bg-white border border-slate-200 p-3 rounded-xl shadow-sm group hover:border-teal-300 transition-all"
                        >
                          <div>
                            <div className="font-bold text-slate-800 text-sm">{rule.reason}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase mt-0.5 tracking-tight">
                              {rule.type === 'single' && `Date: ${formatUkDate(rule.date, '—')}`}
                              {rule.type === 'range' &&
                                `Range: ${formatUkDate(rule.startDate, '—')} - ${formatUkDate(rule.endDate, '—')}`}
                              {rule.type === 'pattern' &&
                                `${rule.frequency === 'biweekly' ? 'Every 2nd' : 'Every'} ${DAYS[rule.dayOfWeek!]} ${rule.frequency === 'biweekly' ? '(from ' + formatUkDate(rule.startFrom, '—') + ')' : ''}`}
                            </div>
                          </div>
                          <button
                            onClick={() => removeRule(rule.id)}
                            className="text-slate-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-slate-50 rounded-b-xl flex justify-end gap-3">
              <button
                onClick={() => setEditingVenue(null)}
                disabled={isSaving}
                className="px-5 py-2 text-slate-600 font-bold hover:bg-slate-100 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!editingVenue) return;
                  const parsedCoordinates = parseCoordinatesDraft(coordinatesDraft);
                  if (parsedCoordinates.hasValue && !parsedCoordinates.isValid) {
                    return showAlertModal(
                      'Coordinates must include a valid latitude (-90 to 90) and longitude (-180 to 180).'
                    );
                  }
                  const venueToSave = {
                    ...editingVenue,
                    schedule: getScheduleWithDefaults(editingVenue.schedule)
                  };
                  if (parsedCoordinates.coords) {
                    venueToSave.coordinates = parsedCoordinates.coords;
                  } else {
                    delete venueToSave.coordinates;
                  }
                  setIsSaving(true);
                  try {
                    if (venueToSave.id) {
                      await Promise.resolve(onUpdateVenue(venueToSave));
                    } else {
                      await Promise.resolve(onAddVenue({ ...venueToSave, id: createId() }));
                    }
                    setEditingVenue(null);
                    await refreshVenues();
                  } catch (err: unknown) {
                    console.error('Venue save error:', err);
                    showAlertModal(
                      normalizeErrorMessage(err, 'Failed to save venue. Please try again.')
                    );
                  } finally {
                    setIsSaving(false);
                  }
                }}
                disabled={isSaving}
                className="bg-slate-900 text-white px-8 py-2 rounded-lg font-bold hover:bg-slate-800 shadow-lg active:scale-95 transition-all"
              >
                {isSaving ? 'Saving...' : 'Save Venue'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h3 className="text-2xl font-bold text-slate-800">Venues & Locations</h3>
          <p className="text-sm text-slate-500 mt-1">
            Manage physical locations, opening hours, and holiday schedules.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search venues..."
              className="pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none w-56 bg-white text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canEdit && (
            <button
              onClick={() =>
                openVenueEditor(
                  {
                    id: '',
                    name: '',
                    address: '',
                    city: '',
                    image: '',
                    schedule: DEFAULT_SCHEDULE
                  },
                  'details'
                )
              }
              className="bg-slate-900 text-white px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 hover:bg-slate-800 shadow-lg active:scale-95 transition-all"
            >
              <Plus className="w-5 h-5" /> Add New Venue
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoadingVenues ? (
          <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
            Loading venues...
          </div>
        ) : venuesError ? (
          <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-red-500">
            {venuesError}
          </div>
        ) : venues.length === 0 ? (
          <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500 italic">
            No venues found.
          </div>
        ) : (
          venues.map((venue) => {
            const mapUrl = getVenueMapUrl(venue, {
              width: 640,
              height: 360,
              zoom: 13
            });
            const imageSrc = mapUrl;
            const mapsLink = getGoogleMapsUrl(venue);

            return (
              <div
                key={venue.id}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden group hover:shadow-xl transition-all border-b-4 border-b-transparent hover:border-b-teal-500 flex flex-col"
              >
                <div className="h-44 bg-slate-100 relative overflow-hidden">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt={`Map of ${venue.name}`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <MapPin className="w-12 h-12 opacity-20" />
                    </div>
                  )}
                  {mapsLink && (
                    <a
                      href={mapsLink}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open ${venue.name} in Google Maps`}
                      className="absolute inset-0 z-10 block"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60 pointer-events-none"></div>
                  <div className="absolute bottom-3 left-3 text-white pointer-events-none">
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-80 mb-0.5">
                      Location ID: {venue.id}
                    </div>
                    <h4 className="font-bold text-lg leading-tight">{venue.name}</h4>
                  </div>
                  {canEdit && (
                    <div className="absolute top-3 right-3 z-20 flex gap-2 translate-y-[-10px] opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                      <button
                        onClick={() => {
                          openVenueEditor(venue, 'details');
                        }}
                        className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white text-slate-700 hover:text-blue-600 shadow-md"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => {
                          await Promise.resolve(onDeleteVenue(venue.id));
                          await refreshVenues();
                        }}
                        className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white text-slate-700 hover:text-red-600 shadow-md"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-slate-600 text-sm mb-3">
                    <MapPin className="w-4 h-4 text-teal-500 shrink-0" />
                    <span className="font-medium">{venue.city}</span>
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed mb-6">
                    {venue.address}
                  </p>

                  <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                        Status
                      </span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                        <span className="text-xs font-bold text-slate-700 uppercase">
                          Operational
                        </span>
                      </div>
                    </div>
                    {canEdit && (
                      <button
                        onClick={() => {
                          openVenueEditor(venue, 'schedule');
                        }}
                        className="text-[10px] font-bold uppercase tracking-widest text-teal-600 hover:text-teal-700 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg transition-colors border border-teal-100"
                      >
                        Calendar & Hours
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        isLoading={isLoadingVenues}
      />
    </div>
  );
};
